import type { TicketReplyDraft } from '@easyhelp/shared';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useId, useRef, useState } from 'react';
import { SECONDARY_BUTTON_SX } from '../../components/accessible-buttons';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { formatDateTime } from '../../components/ticket';
import { useDiscardReplyDraft } from '../../api/reply-drafts';
import { replyDraftArrivalText, replyDraftByline } from './reply-draft-text';
import { WithViaMcpAbbr } from './ViaMcp';

/** Fuori dallo schermo ma letto dai lettori di schermo (come `visuallyHidden` di MUI). */
const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/**
 * Ritardo dell'annuncio: all'apertura del ticket la regione nasce insieme alla bozza e il focus va al
 * titolo; alcuni lettori di schermo (NVDA con Chrome) perdono un cambio immediato di una regione appena
 * creata.
 */
const ANNOUNCE_DELAY_MS = 400;

/** Righe dell'anteprima: il testo intero si legge caricandolo nel composer (i lettori di schermo lo leggono tutto). */
const PREVIEW_LINES = 4;

export interface ReplyDraftBannerProps {
  code: string;
  /** La bozza del ticket (`TicketDetail.replyDraft`), `null` se non c'è. */
  draft: TicketReplyDraft | null;
  /** Il testo del composer: se non è vuoto e non è già la bozza, «Usa la bozza» chiede conferma. */
  composerText: string;
  /** Vero se questa bozza (stesso `updatedAt`) è già caricata nel composer. */
  loaded: boolean;
  /** Carica il testo della bozza nel composer, come risposta. */
  onUse: (draft: TicketReplyDraft) => void;
  /** Porta il focus sul campo del composer. */
  focusComposer: () => void;
}

type Pending = 'replace' | 'discard' | null;

/**
 * La bozza di risposta sopra il composer (DEC-015 decisione 7, PRD-007 US-7, story #159): chi l'ha
 * preparata, da dove («via MCP», con l'abbreviazione espansa come nella cronologia, #156) e quando,
 * un'anteprima del testo e i comandi «Usa la bozza» e «Scarta».
 *
 * - «Usa la bozza» carica il testo nel composer, dove resta modificabile; se il composer contiene già
 *   un altro testo chiede prima conferma (`ConfirmDialog`). Il focus va al campo.
 * - «Scarta» chiede conferma e chiama `DELETE /tickets/:code/reply-draft`; il focus va al campo del
 *   composer, perché il comando sparisce con la bozza.
 * - L'arrivo della bozza, o la sua presenza all'apertura del ticket, è annunciato in una regione
 *   `aria-live="polite"` sempre montata, senza spostare il focus (WCAG 2.2 4.1.3).
 *
 * Fondo `primary.lighter` come le risposte degli operatori nel thread (#121): testo `text.primary`
 * 11,9:1, didascalie `text.secondary` 7,7:1; pulsanti con contrasto AAA e bersagli di 44 px.
 */
export function ReplyDraftBanner({ code, draft, composerText, loaded, onUse, focusComposer }: ReplyDraftBannerProps) {
  const bylineId = useId();
  const discard = useDiscardReplyDraft(code);
  const [pending, setPending] = useState<Pending>(null);
  // Dopo la chiusura del dialogo: al campo se l'azione è andata a buon fine, altrimenti al comando
  // che l'ha aperto (il ritorno automatico di MUI è spento da `onExited`).
  const [focusAfterClose, setFocusAfterClose] = useState(false);
  const useButton = useRef<HTMLButtonElement>(null);
  const discardButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const announcedAt = useRef<string | null>(null);
  const arrivalTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // L'annuncio cambia dopo il montaggio della regione, così i lettori di schermo lo leggono anche
  // all'apertura del ticket; una bozza nuova (altro `updatedAt`) è annunciata di nuovo, il dettaglio
  // riletto con la stessa bozza no.
  useEffect(() => {
    if (!draft || announcedAt.current === draft.updatedAt) return;
    arrivalTimer.current = setTimeout(() => {
      announcedAt.current = draft.updatedAt;
      setAnnouncement(replyDraftArrivalText(draft));
    }, ANNOUNCE_DELAY_MS);
    return () => clearTimeout(arrivalTimer.current);
  }, [draft]);

  /** Un annuncio dell'azione appena fatta: prende il posto di quello dell'arrivo, se non è ancora partito. */
  function announce(text: string) {
    clearTimeout(arrivalTimer.current);
    if (draft) announcedAt.current = draft.updatedAt;
    setAnnouncement(text);
  }

  function load(current: TicketReplyDraft) {
    onUse(current);
    announce('Bozza caricata nel composer: puoi modificarla prima di inviarla.');
  }

  function requestUse() {
    if (!draft) return;
    if (composerText.trim() !== '' && composerText !== draft.text) {
      opener.current = useButton.current;
      setPending('replace');
      return;
    }
    load(draft);
    focusComposer();
  }

  function confirmReplace() {
    if (draft) load(draft);
    setFocusAfterClose(Boolean(draft));
    setPending(null);
  }

  function requestDiscard() {
    discard.reset();
    opener.current = discardButton.current;
    setPending('discard');
  }

  function confirmDiscard() {
    discard.mutate(undefined, {
      onSuccess: () => {
        announce('Bozza di risposta scartata.');
        setFocusAfterClose(true);
        setPending(null);
      },
    });
  }

  function afterClose() {
    // Il comando può essere sparito (bozza scartata o sostituita nel frattempo): allora il campo.
    const back = opener.current?.isConnected ? opener.current : null;
    if (focusAfterClose || !back) focusComposer();
    else back.focus();
    setFocusAfterClose(false);
    opener.current = null;
  }

  const byline = draft ? replyDraftByline(draft) : '';

  return (
    <>
      {/* `aria-live` e non `role="status"`: lo stato visibile del composer resta l'unico `status` della sezione. */}
      <Box aria-live="polite" aria-atomic="true" data-testid="reply-draft-announcer" sx={VISUALLY_HIDDEN}>
        {announcement}
      </Box>

      {draft && (
        <Box
          role="group"
          aria-labelledby={bylineId}
          sx={{
            bgcolor: 'primary.lighter',
            border: 1,
            borderColor: 'primary.light',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <EditNoteOutlinedIcon fontSize="small" aria-hidden sx={{ color: 'primary.dark', mt: '2px' }} />
              <Typography id={bylineId} variant="subtitle2" component="p" sx={{ overflowWrap: 'anywhere' }}>
                <WithViaMcpAbbr>{`${byline}, `}</WithViaMcpAbbr>
                <time dateTime={draft.updatedAt}>{formatDateTime(draft.updatedAt)}</time>
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              data-testid="reply-draft-preview"
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                display: '-webkit-box',
                WebkitLineClamp: PREVIEW_LINES,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {draft.text}
            </Typography>
            {loaded && (
              <Typography variant="caption" color="text.secondary">
                Caricata nel composer: le modifiche restano lì e partono solo con l&apos;invio.
              </Typography>
            )}
            <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Button
                ref={useButton}
                variant="outlined"
                onClick={requestUse}
                sx={{ ...SECONDARY_BUTTON_SX, bgcolor: 'background.paper' }}
              >
                Usa la bozza
              </Button>
              <Button ref={discardButton} variant="text" onClick={requestDiscard} sx={SECONDARY_BUTTON_SX}>
                Scarta
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      <ConfirmDialog
        open={pending === 'replace'}
        title="Sostituire il testo del composer?"
        confirmLabel="Sostituisci con la bozza"
        onConfirm={confirmReplace}
        onClose={() => setPending(null)}
        onExited={afterClose}
      >
        Nel composer c&apos;è già un testo: la bozza lo sostituisce e quello che hai scritto non si recupera.
      </ConfirmDialog>
      <ConfirmDialog
        open={pending === 'discard'}
        title="Scartare la bozza di risposta?"
        confirmLabel="Scarta la bozza"
        confirmColor="error"
        busy={discard.isPending}
        error={discard.isError ? `Bozza non scartata: ${discard.error.message}` : null}
        onConfirm={confirmDiscard}
        onClose={() => setPending(null)}
        onExited={afterClose}
      >
        {draft ? `${byline} si cancella per tutto il team.` : 'La bozza si cancella per tutto il team.'}
        {loaded && ' Il testo già caricato nel composer resta.'}
      </ConfirmDialog>
    </>
  );
}
